import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import TestdataInDbService from './TestdataInDbService'

const InDbConfig: FC<{}> = () => {
  const { t } = useTranslation()
  const [inDbConfigs, setInDbConfigs] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TestdataInDbService.getAllInDbConfigs(setInDbConfigs)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('testdata.indb.config.name'),
        dataField: 'configName',
        sort: true,
        classes: 'position-relative',
      },
      {
        text: t('testdata.indb.config.dbType'),
        dataField: 'dbType',
        sort: true,
      },
      {
        text: t('testdata.indb.config.dbName'),
        dataField: 'dbName',
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.edit),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('edit')}
                onClick={() => editConfigDialog(t, editDialogPropsSetter, setInDbConfigs, row)}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.adapter.edit}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteConfigDialog(t, confirmDialogPropsSetter, setInDbConfigs, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter, editDialogPropsSetter])

  const defaultSorted = [
    {
      dataField: 'policyname',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={inDbConfigs}
        columns={columns}
        caption={t('Database Connections')}
        helpLink={t('testdata.soap.helpLink')}
        captionButtons={[
          {
            onClick: () => createConfigDialog(t, editDialogPropsSetter, setInDbConfigs),
            text: t('new'),
            permissions: Authorization.adapter.edit,
          },
        ]}
        searchPlaceholder='Search Database Connections'
        defaultSorted={defaultSorted}
      />
    </Loading>
  )
}

function createConfigDialog(t: TFunction, editDialogPropsSetter: any, setInDbConfigs: React.Dispatch<any>) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.config.createHeader'),
    onSubmit: TestdataInDbService.createConfig,
    onSubmitExtraArgs: [t, setInDbConfigs],
    data: [
      {
        id: 'configName',
        type: 'text',
        label: t('testdata.indb.config.name'),
        required: true,
      },
      {
        id: 'dbType',
        type: 'select',
        options: ['MARIADB', 'MySQL', 'Microsoft SQL Server', 'IBM DB2', 'Oracle'],
        value: 'MARIADB',
        label: t('testdata.indb.config.dbType'),
        required: true,
      },
      {
        id: 'dbUser',
        type: 'text',
        label: t('username.text'),
        required: true,
      },
      {
        id: 'password',
        type: 'password',
        label: t('password.text'),
        required: true,
      },
      {
        id: 'dbName',
        type: 'text',
        label: t('testdata.indb.config.dbName'),
        required: true,
      },
      {
        id: 'server',
        type: 'text',
        label: t('testdata.indb.config.dbServer'),
        required: true,
      },
      {
        id: 'port',
        type: 'number',
        label: t('testdata.indb.config.dbPort'),
        required: true,
      },
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function editConfigDialog(t: TFunction, editDialogPropsSetter: any, setInDbConfigs: React.Dispatch<any>, row: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('testdata.indb.config.editHeader'),
    onSubmit: TestdataInDbService.updateConfig,
    onSubmitExtraArgs: [t, setInDbConfigs],
    data: [
      {
        id: 'id',
        value: row.id,
        type: 'hidden',
        render: false,
      },
      {
        id: 'configName',
        type: 'text',
        value: row.configName,
        label: t('testdata.indb.config.name'),
        required: true,
      },
      {
        id: 'dbType',
        type: 'select',
        value: row.dbType,
        options: ['MARIADB', 'MySQL', 'Microsoft SQL Server', 'IBM DB2', 'Oracle'],
        label: t('testdata.indb.config.dbType'),
        required: true,
      },
      {
        id: 'dbUser',
        type: 'text',
        value: row.dbUser,
        label: t('username.text'),
        required: true,
      },
      {
        id: 'password',
        type: 'password',
        value: row.password,
        label: t('password.text'),
        required: true,
      },
      {
        id: 'dbName',
        type: 'text',
        value: row.dbName,
        label: t('testdata.indb.config.dbName'),
        required: true,
      },
      {
        id: 'server',
        type: 'text',
        value: row.server,
        label: t('testdata.indb.config.dbServer'),
        required: true,
      },
      {
        id: 'port',
        type: 'number',
        value: row.port,
        label: t('testdata.indb.config.dbPort'),
        required: true,
      },
    ],
    labelGridSize: 5,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteConfigDialog(t: TFunction, confirmDialogPropsSetter: any, setInDbConfigs: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('testdata.indb.config.deleteHeader'),
    body: t('testdata.indb.config.deleteBody', { name: row.configName }),
    submitText: t('yes'),
    onSubmit: TestdataInDbService.deleteConfig,
    onSubmitExtraArgs: [row, t, setInDbConfigs],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

export default InDbConfig
